import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FileDownloadService {

  /**
   * Main entry point used by any component.
   * Accepts the raw API response object (must have .file.data or .file, .fileType, .fileName, .attachmentId)
   * and an optional override for the downloaded file name.
   */
  async downloadFileFromResponse(fileDataModel: any, fileOutputName?: string): Promise<void> {
    const fileType = this.normalizeFileType(fileDataModel && fileDataModel.fileType);
    const fileDataContainer = fileDataModel && fileDataModel.file;
    const fileData: string = fileDataContainer && fileDataContainer.data
      ? fileDataContainer.data
      : fileDataContainer;

    if (!fileData) {
      throw new Error('Invalid file data: no base64 content found');
    }

    const outputName = fileOutputName || this.buildOutputFileName(fileDataModel, fileType);
    const fileBytes = await this.decodeFileBytes(fileData);
    const mimeType = this.getMimeType(fileType);

    // Copy into a plain ArrayBuffer so Blob constructor always accepts it
    const blobBytes = new Uint8Array(fileBytes.byteLength);
    blobBytes.set(fileBytes);
    const blob = new Blob([blobBytes.buffer], { type: mimeType });

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = objectUrl;
    link.download = outputName;
    link.click();
    window.URL.revokeObjectURL(objectUrl);
    link.remove();
  }

  getMimeType(fileType: string): string {
    const mimeMap: Record<string, string> = {
      '.pdf':  'application/pdf',
      '.jpg':  'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png':  'image/png',
      '.gif':  'image/gif',
      '.bmp':  'image/bmp',
      '.webp': 'image/webp',
      '.doc':  'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls':  'application/vnd.ms-excel',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.txt':  'text/plain'
    };
    return mimeMap[fileType] || 'application/octet-stream';
  }

  normalizeFileType(fileTypeValue: string): string {
    if (!fileTypeValue) { return ''; }
    const normalized = fileTypeValue.toLowerCase();
    return normalized.charAt(0) === '.' ? normalized : '.' + normalized;
  }

  async decodeFileBytes(fileData: string): Promise<Uint8Array>;
  async decodeFileBytes(fileData: string, returnBase64: false): Promise<Uint8Array>;
  async decodeFileBytes(fileData: string, returnBase64: true): Promise<string>;
  async decodeFileBytes(fileData: string, returnBase64?: boolean): Promise<Uint8Array | string> {
    // Normalise Base64URL → standard Base64
    let base64 = (fileData || '').replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) { base64 += '='; }

    const binaryString = atob(base64);
    const rawBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      rawBytes[i] = binaryString.charCodeAt(i);
    }

    // Try GZIP decompression; fall back to raw bytes silently
    const DecompressionStreamCtor = (window as any).DecompressionStream;
    if (!DecompressionStreamCtor) {
      return returnBase64 ? base64 : rawBytes;
    }

    try {
      const stream = new DecompressionStreamCtor('gzip');
      const writer = stream.writable.getWriter();
      const reader = stream.readable.getReader();
      // Errors surface through reader.read(); suppress rejections here to avoid unhandled rejections
      writer.write(rawBytes.buffer.slice(0) as ArrayBuffer).catch(() => {});
      writer.close().catch(() => {});

      const chunks: Uint8Array[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) { break; }
        chunks.push(value);
      }

      const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
      const decompressedBytes = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        decompressedBytes.set(chunk, offset);
        offset += chunk.length;
      }

      // If returnBase64 flag is true, convert decompressed bytes to base64
      if (returnBase64) {
        let binaryStr = '';
        for (let i = 0; i < decompressedBytes.length; i++) {
          binaryStr += String.fromCharCode(decompressedBytes[i]);
        }
        return btoa(binaryStr);
      }

      return decompressedBytes;
    } catch {
      return returnBase64 ? base64 : rawBytes;
    }
  }

  private buildOutputFileName(fileDataModel: any, fileType: string): string {
    const attachmentPrefix = fileDataModel && fileDataModel.attachmentId
      ? fileDataModel.attachmentId + '-'
      : '';
    const baseName = (fileDataModel && fileDataModel.fileName) || 'download';
    const fileName = attachmentPrefix + baseName;
    if (fileType && !fileName.toLowerCase().endsWith(fileType)) {
      return fileName + fileType;
    }
    return fileName;
  }
}
